// Trash at /o/:slug/trash (F2 / FR-L2): GET /orgs/{id}/trash, POST /grackles/{id}/restore,
// DELETE /grackles/{id}/purge. A plain member sees only their own trashed items; an org
// admin/owner sees every trashed item in the org (server/src/trash.rs's list_trash).
import { useCallback, useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Callout } from "@grackle/ui";
import { ApiFail, deleteErrorMessage } from "../api/client";
import type { TrashItemOut } from "../api/types";
import { AppShell } from "../components/AppShell";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button, PageLoader, EmptyState } from "../components/ui";
import { useSession } from "../session";
import { listTrash, purgeGrackle, restoreGrackle } from "./api";
import { formatDuration, formatRelativeTime } from "./format";
import "./library.css";

function purgeDateLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export default function TrashPage() {
  const { slug } = useParams();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const [items, setItems] = useState<TrashItemOut[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<TrashItemOut | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);
  const [purging, setPurging] = useState(false);

  const load = useCallback(async (orgId: string) => {
    setLoading(true);
    setError(null);
    try {
      const page = await listTrash(orgId, { limit: 24 });
      setItems(page.items);
      setNextCursor(page.next_cursor);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load the trash.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!org) return;
    void load(org.id);
  }, [org?.id, load]);

  async function loadMore() {
    if (!org || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const page = await listTrash(org.id, { limit: 24, cursor: nextCursor });
      setItems((prev) => [...prev, ...page.items]);
      setNextCursor(page.next_cursor);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load more.");
    } finally {
      setLoadingMore(false);
    }
  }

  async function onRestore(item: TrashItemOut) {
    setRestoringId(item.id);
    setError(null);
    try {
      await restoreGrackle(item.id);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't restore this recording.");
    } finally {
      setRestoringId(null);
    }
  }

  function onPurgeClick(item: TrashItemOut) {
    setPurgeError(null);
    setPurgeTarget(item);
  }

  async function onPurgeConfirm() {
    if (!purgeTarget) return;
    setPurging(true);
    setPurgeError(null);
    try {
      await purgeGrackle(purgeTarget.id);
      setItems((prev) => prev.filter((i) => i.id !== purgeTarget.id));
      setPurgeTarget(null);
    } catch (err) {
      // 409 (blocked by a legal hold, FR-AD5 / SD43) gets the friendly hold copy; any other
      // server reason is shown as-is so the dialog explains why this one can't be purged yet.
      setPurgeError(deleteErrorMessage(err, "Couldn't delete this recording forever."));
    } finally {
      setPurging(false);
    }
  }

  const canRecord = user?.email_verified ?? false;

  if (sessionLoading) return <PageLoader label="Loading trash…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  return (
    <AppShell org={org} user={user} section="trash" canRecord={canRecord}>
      <div className="lib-header">
        <div className="lib-header-copy">
          <h1 className="lib-header-title">Trash</h1>
          <div className="lib-header-sub">
            {loading ? "Loading…" : `${items.length} recording${items.length === 1 ? "" : "s"} · `}
            Deleted forever 30 days after they're trashed.
          </div>
        </div>
      </div>

      {error ? (
        <div className="lib-error">
          <Callout tone="danger" icon="warning-circle">
            {error}
          </Callout>
        </div>
      ) : null}

      {loading ? (
        <PageLoader label="Loading trash…" />
      ) : items.length === 0 ? (
        <EmptyState
          className="lib-empty"
          icon="trash"
          title="Trash is empty"
          body="Recordings you delete show up here for 30 days before they're gone for good."
        />
      ) : (
        <>
          <div className="trash-list">
            {items.map((item) => (
              <div className="trash-row" key={item.id}>
                <div className="trash-row__body">
                  <div className="trash-row__title">{item.title || "Untitled"}</div>
                  <div className="trash-row__meta">
                    {item.owner.name} · Trashed {formatRelativeTime(item.trashed_at)}
                    {item.duration_ms != null ? ` · ${formatDuration(item.duration_ms)}` : ""} · Deleted forever on{" "}
                    {purgeDateLabel(item.purge_after)}
                  </div>
                </div>
                <div className="trash-row__actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void onRestore(item)}
                    loading={restoringId === item.id}
                    disabled={restoringId !== null}
                  >
                    Restore
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onPurgeClick(item)} disabled={restoringId !== null}>
                    Delete forever
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {nextCursor ? (
            <div className="lib-load-more">
              <Button variant="secondary" onClick={() => void loadMore()} loading={loadingMore}>
                Load more
              </Button>
            </div>
          ) : null}
        </>
      )}

      <ConfirmDialog
        open={purgeTarget !== null}
        title="Delete forever"
        message={
          <>
            {`Delete "${purgeTarget?.title || "Untitled"}" forever? This can't be undone.`}
            {purgeError ? <Callout tone="danger" icon="warning-circle">{purgeError}</Callout> : null}
          </>
        }
        confirmLabel="Delete forever"
        danger
        loading={purging}
        onClose={() => {
          setPurgeTarget(null);
          setPurging(false);
        }}
        onConfirm={onPurgeConfirm}
      />
    </AppShell>
  );
}
